import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import * as d3 from "d3";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Box,
  Typography,
  TextField,
  Paper,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

const PaymentSingleChart = () => {

  const currentMonthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const currentMonthEnd = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);

  const [names, setNames] = useState([]);
  const [selectedName, setSelectedName] = useState(null);
 const [fromDate, setFromDate] = useState(currentMonthStart);
  const [toDate, setToDate] = useState(currentMonthEnd);
  const [paymentData, setPaymentData] = useState([]);
  const chartRef = React.useRef();

  const [selectedCategory, setSelectedCategory] = useState({ value: "Single", label: "Single" });

  const categoryOptions = [
    { value: "All", label: "All" },
    { value: "Single", label: "Single" },
    { value: "Membership", label: "Membership" },
];

const tooltipRef = useRef();

useEffect(() => {
  fetchNames(selectedCategory.value);
  setSelectedName(null);
}, [selectedCategory]);

const fetchNames = async (category) => {
  try {
      let response;

      if (category === "Membership") {
          response = await axios.get(`https://us-central1-balajibadmintonacademy.cloudfunctions.net/api/getMembershipData`);
          const membershipData = response.data || {};

          const namesSet = new Set();
          Object.values(membershipData).forEach((membership) => {
              membership.names.forEach((name) => namesSet.add(name));
          });

          const personsResponse = await axios.get(`https://us-central1-balajibadmintonacademy.cloudfunctions.net/api/getPersons`);
          const persons = personsResponse.data || {};

          const filteredNames = Object.values(persons)
              .filter((person) => namesSet.has(person.name))
              .map((person) => ({
                  id: person.personId,
                  label: `${person.personId}-${person.name}`,
                  value: person.name,
              }));
          setNames(filteredNames);
      } else { 
          response = await axios.get(`https://us-central1-balajibadmintonacademy.cloudfunctions.net/api/getPersons`);
          const persons = response.data || {};

          const membershipResponse = await axios.get(`https://us-central1-balajibadmintonacademy.cloudfunctions.net/api/getMembershipData`);
          const membershipData = membershipResponse.data || {};
          const membershipNamesSet = new Set();
          Object.values(membershipData).forEach((membership) => {
              membership.names.forEach((name) => membershipNamesSet.add(name));
          });

          const singleNamesArray = Object.values(persons)
              .filter((person) => !membershipNamesSet.has(person.name)) // Exclude Membership names
              .map((person) => ({
                  id: person.personId,
                  label: `${person.personId}-${person.name}`,
                  value: person.name,
              }));

          setNames(singleNamesArray);
      }
  } catch (error) {
      console.error("Error fetching names:", error);
      setNames([]);
  }
};

useEffect(() => {
  fetchPaymentData();
}, [fromDate, toDate, selectedName]);

  const fetchPaymentData = async () => {
    try {
      const response = await axios.get("https://us-central1-balajibadmintonacademy.cloudfunctions.net/api/getPayments");
      setPaymentData(response.data);
    } catch (error) {
      console.error("Error fetching payment data:", error);
    }
  };

  // Filter and aggregate data
  const filteredData = paymentData.filter((entry) => {
    const matchesName = selectedName
      ? entry.personId.toString() === selectedName.id.toString()
      : true;

    const matchesDate = entry.date.some((entryDate) => {
      const parsedDate = new Date(entryDate);
      return parsedDate >= fromDate && parsedDate <= toDate;
    });

    return matchesName && matchesDate;
  });

  const aggregatedData = [];
  filteredData.forEach((entry) => {
    entry.date.forEach((date, index) => {
      const parsedDate = new Date(date);
      if (parsedDate >= fromDate && parsedDate <= toDate) {
        const paidAmount = parseFloat(entry.paidAmount[index] || 0);
        const remainingAmount = parseFloat(entry.remainingAmount || 0);
        const isPaid = remainingAmount === 0;
  
        const existingDate = aggregatedData.find((d) => d.date === date);
        if (existingDate) {
          existingDate.Paid += isPaid ? 1 : 0;
          existingDate.Unpaid += isPaid ? 0 : 1;
          existingDate.totalAmount += paidAmount + remainingAmount;
          existingDate.totalPaid += paidAmount;
        } else {
          aggregatedData.push({
            date,
            Paid: isPaid ? 1 : 0,
            Unpaid: isPaid ? 0 : 1,
            totalAmount: paidAmount + remainingAmount,
            totalPaid: paidAmount,
          });
        }
      }
    });
  });
  
  // Calculate cumulative totals for the green line
  aggregatedData.sort((a, b) => new Date(a.date) - new Date(b.date));
  aggregatedData.reduce((cumulative, current) => {
    current.cumulativeTotalAmount = cumulative.totalAmount + current.totalAmount;
    current.cumulativeTotalPaid = cumulative.totalPaid + current.totalPaid;
    return current;
  }, { totalAmount: 0, totalPaid: 0 });
  
  
  const totalPaid = aggregatedData.reduce((sum, entry) => sum + entry.Paid, 0);
  const totalUnpaid = aggregatedData.reduce((sum, entry) => sum + entry.Unpaid, 0);

  useEffect(() => {
    if (!chartRef.current || aggregatedData.length === 0) return;

    const svg = d3.select(chartRef.current);
    const margin = { top: 20, right: 60, bottom: 40, left: 50 };
    const width = 600 - margin.left - margin.right;
    const height = 400 - margin.top - margin.bottom;

    svg.selectAll("*").remove();

    // Aggregate data for line charts
    const lineData = aggregatedData.map((d) => ({
        date: d.date,
        cumulativeTotalAmount: d.cumulativeTotalAmount, // Use cumulative totalAmount
        cumulativeTotalPaid: d.cumulativeTotalPaid,     // Use cumulative totalPaid
    }));

    // X Scale
    const x = d3.scaleBand()
        .domain(aggregatedData.map((d) => d.date))
        .range([0, width])
        .padding(0.2);

    // Y Scale for Bar Chart
    const yBar = d3.scaleLinear()
        .domain([0, d3.max(aggregatedData, (d) => d.Paid + d.Unpaid)])
        .nice()
        .range([height, 0]);

    // Y Scale for Line Chart
    const yLine = d3.scaleLinear()
    .domain([0, d3.max(aggregatedData, (d) => Math.max(d.cumulativeTotalAmount, d.cumulativeTotalPaid))])
    .nice()
    .range([height, 0]);
  
  

    const color = d3.scaleOrdinal()
        .domain(["Paid", "Unpaid"])
        .range(["#008000", "#f44336"]);

    // Create chart group
    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    // Add X axis
    g.append("g")
        .attr("transform", `translate(0,${height})`)
        .call(d3.axisBottom(x));

    // Add Y axis for Bar Chart
    g.append("g").call(d3.axisLeft(yBar));

    // Add Y axis for Line Chart (on the right side)
    g.append("g")
        .attr("transform", `translate(${width},0)`)
        .call(d3.axisRight(yLine));

    // Draw Bar Chart
    g.selectAll(".bar-group")
        .data(aggregatedData)
        .enter()
        .append("g")
        .attr("transform", (d) => `translate(${x(d.date)},0)`)
        .selectAll("rect")
        .data((d) => [
            { status: "Paid", value: d.Paid, date: d.date },
            { status: "Unpaid", value: d.Unpaid, date: d.date },
        ])
        .enter()
        .append("rect")
        .attr("x", (d) => (x.bandwidth() / 4) * (d.status === "Paid" ? 0 : 2))
        .attr("width", x.bandwidth() / 2)
        .attr("y", yBar(0))
        .attr("height", 0)
        .attr("fill", (d) => color(d.status))
        .transition()
        .duration(2000)
        .attr("y", (d) => yBar(d.value))
        .attr("height", (d) => height - yBar(d.value));

    // Draw Total Amount Line (Red)
    const lineAmount = d3.line()
    .x((d) => x(d.date) + x.bandwidth() / 2)
    .y((d) => yLine(d.cumulativeTotalAmount));

    g.append("path")
        .datum(lineData)
        .attr("fill", "none")
        .attr("stroke", "red")
        .attr("stroke-width", 2)
        .attr("d", lineAmount);

    // Draw Total Paid Line (Green)
    const linePaid = d3.line()
    .x((d) => x(d.date) + x.bandwidth() / 2)
    .y((d) => yLine(d.cumulativeTotalPaid));

    g.append("path")
        .datum(lineData)
        .attr("fill", "none")
        .attr("stroke", "green")
        .attr("stroke-width", 2)
        .attr("d", linePaid);

        const tooltip = d3.select("#tooltip");

        g.selectAll(".dot")
            .data(lineData)
            .enter().append("circle")
            .attr("class", "dot")
            .attr("cx", (d) => x(d.date) + x.bandwidth() / 2)
            .attr("cy", (d) => yLine(d.cumulativeTotalPaid))
            .attr("r", 5)
            .attr("fill", "green")
            .on("mouseover", (event, d) => {
                tooltip.style("visibility", "visible")
                    .html(`Date: ${d.date}<br>Total Paid: ${d.cumulativeTotalPaid}`)
                    .style("top", `${event.pageY + 10}px`)
                    .style("left", `${event.pageX + 10}px`);
            })
            .on("mousemove", (event) => {
                tooltip.style("top", `${event.pageY + 10}px`).style("left", `${event.pageX + 10}px`);
            })
            .on("mouseout", () => tooltip.style("visibility", "hidden"));

 
}, [aggregatedData]);


  const handleNameChange = (selectedOption) => {
    setSelectedName(selectedOption);
  };

  const tableData = paymentData.map((entry, index) => ({
    id: index + 1, // S.No
    totalPaid: entry.paidAmount.reduce((sum, amt) => sum + parseFloat(amt || 0), 0),
    totalUnpaid: parseFloat(entry.remainingAmount || 0),
  }));

  const columns = [
    { field: "id", headerName: "S.No", width: 100 },
    { field: "totalPaid", headerName: "Total Paid", flex: 1, type: "number" },
    { field: "totalUnpaid", headerName: "Total Unpaid", flex: 1, type: "number" },
  ];
  
  return (
    <div className="Paymentperson" style={{ marginTop: "80px" }}>
      <h2 className="text-center mb-4">Person Payment</h2>
      <div className="datepayment" style={{ display: "flex", marginBottom: "20px" }}>
        <div>
          <label>From Date:</label>
          <DatePicker selected={fromDate} onChange={(date) => setFromDate(date)} />
        </div>
        <div>
          <label>To Date:</label>
          <DatePicker selected={toDate} onChange={(date) => setToDate(date)} />
        </div>
        <div>
                    <Select 
                        options={categoryOptions} 
                        value={selectedCategory} 
                        onChange={setSelectedCategory} 
                        placeholder="Select Category" 
                    />
                </div>
       
      </div>
      <div>
      <div  className="dateattendance" style={{ display: "flex", marginBottom: "20px" }}>
          <Select options={names} value={selectedName} onChange={handleNameChange} placeholder="Select or search a name" />
        </div>
      </div>

      <div className="chart-legend" style={{ display: "flex", alignItems: "end", marginTop: "20px", right: "0" }}>
                <div style={{ display: "flex", alignItems: "center", marginRight: "15px" }}>
                    <span
                        style={{
                            width: "15px",
                            height: "15px",
                            backgroundColor: "#008000",
                            display: "inline-block",
                            marginRight: "5px",
                            borderRadius: "3px",
                        }}
                    ></span>
                    <span>Paid</span>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                        style={{
                            width: "15px",
                            height: "15px",
                            backgroundColor: "#f44336",
                            display: "inline-block",
                            marginRight: "5px",
                            borderRadius: "3px",
                        }}
                    ></span>
                    <span>Unpaid</span>
                </div>
            </div>
            <h2 className="text-center mb-4">Daily Analysis</h2>  
            <div
                ref={tooltipRef}
                style={{
                    position: "absolute",
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                    color: "white",
                    padding: "8px",
                    borderRadius: "4px",
                    visibility: "hidden",
                    pointerEvents: "none",
                    zIndex: 10,
                }}
            ></div>
      <svg ref={chartRef} width="600" height="400" className="singlechart"></svg>

      <div className="payment-table mb-4">
        <table border="1" width="100%" style={{ marginTop: "20px" }} className="subtable">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Total Paid</th>
              <th>Total Unpaid</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>1</td>
              <td>{totalPaid}</td>
              <td>{totalUnpaid}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PaymentSingleChart;
